import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Nav from "components/layout/Nav";
import { YoutubeVideosMenubar } from "components/layout/MenuBar";
import { DataTable } from "components/shared/table";
import { fetchVideo, deleteVideo } from "../../../Slices/youtubeVideo.slice";
import { useNavigate } from "react-router-dom";
import { CreateLocation } from "components/shared/buttons";
import { AddVideo } from "components/forms/forms";

const Components = {
    videoTable: "videoTable",
};

const YoutubeVideos = () => {
    const [activeComponent, setActiveComponent] = useState(Components.videoTable);
    const [showForm, setShowForm] = useState(false);
    const [isModelOpen, setIsModelOpen] = useState(false);
    const [selectedVideoLink, setSelectedVideoLink] = useState(null);
    const [editFormData, setEditFormData] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { status, error, videos } = useSelector((state) => state.video);

    // ✅ Fetch video data when component mounts
    useEffect(() => {
        if (!videos.length) {
            dispatch(fetchVideo());
        }
    }, [dispatch, videos.length]);

    const filteredVideos = searchTerm.trim().length < 2
        ? videos
        : videos?.filter((video) =>
            [video.location, video.category].some((field) =>
                field?.toLowerCase().includes(searchTerm.toLowerCase())
            )
        );

    // ✅ Handle video deletion
    const handleDelete = (id) => {
        dispatch(deleteVideo(id));
    };

    // ✅ Open video popup
    const openModelPopup = (videoLink) => {
        try {
            let videoId = null;
            const url = new URL(videoLink);
            if (url.hostname.includes("youtube.com")) {
                videoId = url.searchParams.get("v");
            }
            if (url.hostname.includes("youtu.be")) {
                videoId = url.pathname.slice(1);
            }
            if (videoId) {
                setSelectedVideoLink(`https://www.youtube.com/embed/${videoId}`);
                setIsModelOpen(true);
            } else {
                console.error("Invalid YouTube URL:", videoLink);
            }
        } catch (error) {
            console.error("Error parsing video link:", error);
        }
    };

    const closeVideoPopup = () => {
        setSelectedVideoLink(null);
        setIsModelOpen(false);
    };

    // ✅ Handle Edit Video
    const editClick = (data) => {
        setEditFormData(data);
        setShowForm(true);
    };

    // ✅ Handle Adding a New Video
    const handleAddVideoClick = () => {
        setEditFormData(null);
        setShowForm(true);
    };

    const renderComponent = () => {
        switch (activeComponent) {
            case Components.videoTable:
                return (
                    <>
                        <CreateLocation
                            buttonText="Add Video"
                            formComponent={AddVideo}
                            showForm={showForm}
                            setShowForm={handleAddVideoClick}
                        />

                        {showForm && <AddVideo setShowForm={setShowForm} editFormData={editFormData} />}

                        {/* ✅ Video Popup */}
                        {isModelOpen && (
                            <div className="modal-overlay">
                                <div className="modal-content">
                                    <button className="close-button" onClick={closeVideoPopup}>
                                        &times;
                                    </button>
                                    {selectedVideoLink && (
                                        <iframe
                                            width="560"
                                            height="315"
                                            src={selectedVideoLink}
                                            title="YouTube video player"
                                            frameBorder="0"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                        ></iframe>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* ✅ Show filtered or all videos */}
                        <DataTable
                            locations={filteredVideos} // ✅ Now properly handles search
                            editClick={editClick}
                            viewClick={(videoLink) => openModelPopup(videoLink)}
                            deleteConfirmation={handleDelete}
                            showVideoColumn={true}
                        />
                    </>
                );
            default:
                return <div>Select a component from the menu.</div>;
        }
    };

    return (
        <>
            <Nav />
            <div className="row">
                <YoutubeVideosMenubar setActiveComponent={setActiveComponent} setSearchTerm={setSearchTerm} />
                <main style={{ width: "calc(100vw - 240px)", backgroundColor: "#D0D5DD" }}>
                    {status === "loading" && <p>Loading videos...</p>}
                    {error && <p style={{ color: "red" }}>Error: {error}</p>}
                    {renderComponent()}
                </main>
            </div>
        </>
    );
};

export default YoutubeVideos;
